import React, { useState } from "react";

import Button, { ButtonTypes } from "../Button";
import { ErrorState } from "../States";
import { CashbackAdjustment } from "../../services/accounts/types";
import { useGetCashbackAdjustmentsQuery } from "../../services/accounts/accountsApi";
import { useGetCashbackSummaryQuery } from "../../services/transactions/transactionsApi";
import { formatCurrency } from "../../utils";
import {
  isCashbackSectionVisible,
  isInvestedCashback,
} from "../../utils/cashback";
import { AddCashbackPanel } from "./AddCashbackPanel";
import { CashbackAdjustmentList } from "./CashbackAdjustmentList";
import { CASHBACK_LOAD_ERROR, cashbackBuysHrefOf } from "./constants";
import { CorrectionDeletePopUp } from "./CorrectionDeletePopUp";
import {
  SectionCard,
  SectionHeader,
  SectionTitle,
  Stat,
  StatLabel,
  StatLink,
  Stats,
  StatValue,
} from "./styles";
import { CashbackSectionProps } from "./types";
import { useCorrectionDelete } from "./useCorrectionDelete";

export const CashbackSection: React.FC<CashbackSectionProps> = ({
  accountId,
  cashbackMode,
}) => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const correctionDelete = useCorrectionDelete({ accountId });
  const {
    currentData: summary,
    isError: isSummaryError,
    refetch: refetchSummary,
  } = useGetCashbackSummaryQuery(accountId);
  const {
    currentData: adjustments,
    isError: isAdjustmentsError,
    refetch: refetchAdjustments,
  } = useGetCashbackAdjustmentsQuery(accountId);

  if (isSummaryError || isAdjustmentsError) {
    const retry = async () => {
      await Promise.all([refetchSummary(), refetchAdjustments()]);
    };
    return (
      <SectionCard aria-label="Cashback">
        <SectionTitle>Cashback</SectionTitle>
        <ErrorState message={CASHBACK_LOAD_ERROR} onRetry={retry} />
      </SectionCard>
    );
  }

  if (!summary || !adjustments) {
    return null;
  }

  const isVisible = isCashbackSectionVisible({
    mode: cashbackMode,
    invested: summary.invested,
    adjustmentCount: adjustments.length,
  });
  if (!isVisible) {
    return null;
  }

  const canAddCashback = !isInvestedCashback(cashbackMode);
  const hasInvested = summary.invested > 0;

  const requestDelete = (adjustment: CashbackAdjustment) =>
    correctionDelete.requestDelete({
      ...adjustment,
      date: adjustment.adjustmentDate,
    });

  return (
    <SectionCard aria-label="Cashback">
      <SectionHeader>
        <SectionTitle>Cashback</SectionTitle>
        {canAddCashback && (
          <Button
            type={ButtonTypes.link}
            size="small"
            onClick={() => setIsAddOpen(true)}
          >
            + Add cashback
          </Button>
        )}
      </SectionHeader>

      <Stats>
        <Stat>
          <StatLabel>Invested</StatLabel>
          <StatValue>
            {formatCurrency(summary.invested)}
            {hasInvested && (
              <StatLink href={cashbackBuysHrefOf(accountId)}>
                View buys
              </StatLink>
            )}
          </StatValue>
        </Stat>
      </Stats>

      {adjustments.length > 0 && (
        <CashbackAdjustmentList
          adjustments={adjustments}
          onDelete={requestDelete}
        />
      )}

      {isAddOpen && (
        <AddCashbackPanel
          accountId={accountId}
          isOpen
          onClose={() => setIsAddOpen(false)}
        />
      )}

      <CorrectionDeletePopUp correctionDelete={correctionDelete} />
    </SectionCard>
  );
};
